// 치과 구독 잠금 게이트 (2026-08-21 대표 확정 — 9개 기능 잠금, 2026-10-04 요금 변경: 신규 치과 월 99,000원)
// 2026-10-30 구독 플랜제(대표 결정): 치과는 무료 — billingPolicy.decideBilling 이 모든 치과에 clinic_free 를 돌려주므로 아래 잠금 화면은 실제로 나오지 않는다(코드는 보존).
// - 스위치: paywallActive(features) = allFree !== true (계획_가입승인결제흐름 W7, 대표 승인 10-15 — 종전 allFree·clinicSubscription 두 스위치를 하나로).
//   allFree=true(현재 운영값)면 전 계정 현행 그대로 열림. allFree=false 가 되면 요금표 있는 신규 치과의 체험 종료 후만 잠긴다(기존 치과·면제는 그대로 열림).
// - 판정: 요금 판정표(utils/billingPolicy.js — 서버 billing-policy.js 복제, 계획_요금변경 §3·W4)
//   통과 = 치과 아님 / allFree / 베타(아이반+연결 치과) / 면제(subscriptionExempt) / 요금표 없는 기존 치과(별도 통지 시까지 무료)
//        / 요금표 있는 신규 치과의 체험 기간 / 유효 구독(active·trial, 해지했어도 만료 전 cancelled)
//   직원은 소속 업체(companyId) 대표의 요금표·면제·구독으로 판정 — 단 본인 요금표가 있으면 본인 기준(billingPolicy.resolveBillingOwner, 보안 P1-1)
// - 잠금 화면: 진짜 데이터는 로드하지 않는다 — CSS 블러는 장식일 뿐이라 실데이터를 깔면
//   개발자도구로 노출된다(보안). 정적 샘플 행을 블러로 깔아 "궁금하게"만 만든다 (대표 확정).
// - 기존 게이트 체계(stage/StageRoute)와 별개 축: 이건 '요금 잠금'이라 clinicSubscription 플래그 전용.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { getFeatures, paywallActive, isBetaUser } from '../utils/features';
import { CONTACT_PHONE } from './common/SiteFooter';
import { loadBillingDecision, isFreeNow, isLiveSub, decisionHasTariff, TARIFFS } from '../utils/billingPolicy';
import { Lock } from 'lucide-react';

const isClinicType = (u) => {
    const t = u?.businessType || u?.companyBusinessType;
    return t === 'clinic' || t === 'dental' || t === '치과';
};

// 유효 구독 여부 — active·trial·cancelled 중 만료 전 문서 (해지해도 이미 낸 기간까지는 이용, 약관 v6.2 제4조의3 ② — App.js와 같은 규칙)
async function hasLiveSubscription(uid) {
    try {
        const snap = await getDocs(query(
            collection(db, 'appSubscriptions'),
            where('userId', '==', uid),
            where('status', 'in', ['active', 'trial', 'cancelled'])
        ));
        const now = new Date();
        // 판정 규칙은 billingPolicy.isLiveSub 한 곳 (서버 isLiveSub 와 같은 규칙 + BILLING_GRACE_DAYS)
        return snap.docs.some(d => isLiveSub(d.data(), now));
    } catch (e) {
        console.warn('구독 조회 실패 — 안전측(열림) 처리:', e?.message);
        return true;   // 조회 실패로 유료 고객을 잠그는 사고 방지 (조용한 실패 금지: 로그 기록)
    }
}

// lockedRender(선택): 잠금 판정일 때 잠금 카드 대신 그릴 화면 — 「받은 명세서」 읽기 전용 개방용 (대표 10-15 "응", 법무 R4).
//   주면 잠금 카드·블러 샘플을 그리지 않고 lockedRender() 를 돌려준다. 없으면 종전 잠금 카드.
export default function ClinicSubGate({ user, title = '이 기능', children, lockedRender }) {
    const navigate = useNavigate();
    // null=판정 중(그동안 children 렌더 안 함 — 잠금 대상 데이터 로드 방지), true=잠금, false=통과
    const [locked, setLocked] = useState(null);
    // 잠금 카드에 표시할 월 금액(VAT 포함) — 서버 견적(getSubscriptionQuote) 우선, 없으면 가입 당시 요금표 금액
    const [amount, setAmount] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (!user || !isClinicType(user)) { if (alive) setLocked(false); return; }
                const features = await getFeatures();
                // 요금 잠금 미적용(allFree=true) — 치과 구독 잠금 전체 해제. W7: clinicSubscription 의존 제거(paywallActive 한 곳)
                if (!paywallActive(features)) { if (alive) setLocked(false); return; }
                // 설정 읽기 실패 = 열림(fail-open) — 아래 loadBillingDecision 도 features_read_failed 로 열지만 명시
                if (features?._readFailed === true) { if (alive) setLocked(false); return; }
                // 요금 판정표: 면제·요금표 없는 기존 치과·체험 기간이면 통과 (읽기 실패도 통과 — 서버도 청구 안 함, 기록은 billingPolicy가 남김)
                const decision = await loadBillingDecision(user, { features });
                // 2026-10-30 대표 결정: 치과는 무료(clinic_free) — 게이트·안내 없이 바로 children (isFreeNow 로도 통과하지만 명시)
                if (decision?.reason === 'clinic_free' || (decision?.kind === 'clinic' && !decision?.billable)) { if (alive) setLocked(false); return; }
                if (isFreeNow(decision)) { if (alive) setLocked(false); return; }
                // 베타 — 판정 순서는 서버와 같게(요금표 → overseas): 요금표가 있으면 해외(labKind) 단축 통과 무시 (보안 P1-1)
                if (isBetaUser(features, user, user?.email, { ignoreOverseas: decisionHasTariff(decision) })) { if (alive) setLocked(false); return; }
                const uid = decision.ownerUid || user?.companyId || user?.uid;
                const live = await hasLiveSubscription(uid);
                if (!alive) return;
                setLocked(!live);
                if (!live) {
                    setAmount(decision.amount || TARIFFS['clinic_99000_v6.2'].amount);
                    // 대표 계정(본인 요금표가 있어 본인 기준인 계정 포함)만 서버 견적 조회 (견적은 본인 계정 기준 callable)
                    if (uid === user?.uid) {
                        try {
                            const res = await httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'getSubscriptionQuote')({});
                            const q = res?.data;
                            if (alive && q && typeof q.amount === 'number' && q.amount > 0) setAmount(q.amount);
                        } catch (e) {
                            console.warn('구독 견적 조회 실패 — 요금표 금액으로 표시:', e?.message);
                        }
                    }
                }
            } catch (e) {
                console.warn('구독 게이트 판정 실패 — 안전측(열림):', e?.message);
                if (alive) setLocked(false);
            }
        })();
        return () => { alive = false; };
    }, [user]);

    if (locked === null) return null;           // 판정 중 — 잠금 대상 화면(데이터 로드)을 미리 그리지 않는다
    if (locked === false) return children;
    if (typeof lockedRender === 'function') return lockedRender();

    // ── 잠금 화면: 정적 샘플(가짜) 행 블러 + 구독 안내 카드 ──
    const sampleRows = [
        ['김O수', '#16 지르코니아 크라운', '60,000원'],
        ['이O영', '#11,21 이맥스 크라운', '160,000원'],
        ['박O진', '#46 골드 크라운', '466,000원'],
        ['최O희', '#36,37 지르코니아 크라운', '120,000원'],
        ['정O호', '#26 임플란트 크라운', '90,000원'],
        ['강O미', '#15 지르코니아 인레이', '50,000원'],
    ];
    return (
        <div style={{ position: 'relative', minHeight: 480, padding: '30px 20px' }}>
            {/* 샘플 배경 (실데이터 아님 — 블러 전용 장식) */}
            <div aria-hidden="true" style={{ filter: 'blur(7px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.75, maxWidth: 860, margin: '0 auto' }}>
                <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: 22 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 14 }}>{title}</div>
                    {sampleRows.map((r, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 4px', borderBottom: '1px solid #F1F5F9', fontSize: 14 }}>
                            <b>{r[0]}</b><span style={{ flex: 1, color: '#475569' }}>{r[1]}</span><b>{r[2]}</b>
                        </div>
                    ))}
                </div>
            </div>
            {/* 잠금 카드 */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #E0E7FF', boxShadow: '0 12px 40px rgba(15,23,42,.14)', padding: '30px 34px', maxWidth: 400, textAlign: 'center' }}>
                    <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                        <Lock size={24} color="#4F46E5" />
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>{title}은 구독 치과에 열리는 기능입니다</div>
                    <div style={{ fontSize: 13.5, color: '#64748B', lineHeight: 1.7, marginBottom: 18 }}>
                        월 <b style={{ color: '#4F46E5' }}>{`${(amount || TARIFFS['clinic_99000_v6.2'].amount).toLocaleString('ko-KR')}원`}</b>(부가세 포함)으로<br />도착 예정·작업 공정·명세서·분석까지 전부 열립니다.
                    </div>
                    <button
                        onClick={() => navigate('/subscription')}
                        style={{ width: '100%', background: '#6366F1', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 0', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' }}
                    >구독하고 열기</button>
                    <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 10 }}>의뢰서 작성·보낸 의뢰서는 계속 무료입니다</div>
                    {/* 법무 §2 P1 — 잠금 화면에도 자료 열람·내려받기·삭제 요청 경로(고객센터)를 보인다 */}
                    <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 6, lineHeight: 1.6 }}>
                        {`이미 받은 자료의 열람·내려받기나 삭제가 필요하면 고객센터(${CONTACT_PHONE})로 요청해 주세요.`}
                    </div>
                </div>
            </div>
        </div>
    );
}
